import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowLeft,
  BatteryMedium,
  CheckCircle2,
  ChevronDown,
  Download,
  ExternalLink,
  FileText,
  ImageIcon,
  Mail,
  MessageSquare,
  Plus,
  Send,
  Settings,
  Trash2,
  UserMinus,
  X
} from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { AgeIndicator, formatDateTime, InitialsAvatar } from '../components/ui/queueUi'
import {
  agentNameById,
  bucketForStatus,
  customerEmailHref,
  PlanBadge,
  predefinedTags,
  statusLabel,
  supportAgentLabel,
  tagChipClass,
  TicketStatusBadge,
  ticketRef,
  ticketStatusOptions
} from '../components/support/supportUi'
import { useAuth } from '../hooks/useAuth'
import {
  addTicketNote,
  assignTicket,
  createSupportMacro,
  deleteSupportMacro,
  getSupportAgents,
  getSupportMacros,
  getSupportTicket,
  linkUserToTicket,
  sendAgentMessage,
  unassignTicket,
  unlinkUserFromTicket,
  updateSupportMacro,
  updateTicket,
  updateTicketTags,
  type SupportAdminThreadResponse,
  type SupportAgent,
  type SupportMacro,
  type SupportMessage,
  type SupportNote,
  type SupportTicketStatus
} from '../lib/api/support'

const formatTime = (value: string) => {
  try {
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(new Date(value))
  } catch {
    return value
  }
}

const formatDay = (value: string) => {
  try {
    return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(value))
  } catch {
    return value
  }
}

const localDayKey = (value: string) => {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
}

const formatBatteryLevel = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value) ? `${Math.round(value)}%` : 'Not captured'

const batteryModeLabel = (value?: boolean | null) =>
  value == null ? 'Mode not captured' : value ? 'Battery saver on' : 'Normal mode'

type TimelineEntry =
  | { kind: 'message'; at: string; message: SupportMessage }
  | { kind: 'note'; at: string; note: SupportNote }

function SidebarSection({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="border-b border-border/50 pb-4 last:border-b-0 last:pb-0">
      <div className="mb-2.5 flex items-center justify-between">
        <h4 className="font-mono text-[10.5px] font-bold uppercase tracking-widest text-muted-foreground">{title}</h4>
        {action}
      </div>
      {children}
    </section>
  )
}

function MessageAttachments({ message }: { message: SupportMessage }) {
  if (message.attachments.length === 0) return null
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {message.attachments.map((attachment) => {
        const isImage = attachment.contentType.startsWith('image/')
        if (isImage && attachment.downloadUrl) {
          return (
            <a key={attachment.attachmentId} href={attachment.downloadUrl} target="_blank" rel="noopener noreferrer" className="group relative block">
              <img
                src={attachment.downloadUrl}
                alt={attachment.fileName}
                className="h-20 w-20 rounded-lg border border-border/50 object-cover transition group-hover:border-primary"
              />
              <span className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/50 opacity-0 transition group-hover:opacity-100">
                <ExternalLink className="h-5 w-5 text-white" />
              </span>
            </a>
          )
        }
        return (
          <div key={attachment.attachmentId} className="flex items-center gap-2 rounded-lg border border-border/50 bg-background px-3 py-2">
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
            <div className="min-w-0">
              <p className="max-w-40 truncate text-xs font-medium" title={attachment.fileName}>{attachment.fileName}</p>
              <p className="text-[10px] text-muted-foreground">{(attachment.sizeBytes / 1024).toFixed(1)} KB</p>
            </div>
            {attachment.downloadUrl && (
              <a href={attachment.downloadUrl} target="_blank" rel="noopener noreferrer" className="rounded-full p-1 hover:bg-muted" title="Download">
                <Download className="h-3.5 w-3.5 text-muted-foreground" />
              </a>
            )}
          </div>
        )
      })}
    </div>
  )
}

export const SupportTicketPage = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { ticketId } = useParams<{ ticketId: string }>()
  const [ticketDetail, setTicketDetail] = useState<SupportAdminThreadResponse | null>(null)
  const [detailLoading, setDetailLoading] = useState(true)
  const [agents, setAgents] = useState<SupportAgent[]>([])
  const [assignmentSaving, setAssignmentSaving] = useState(false)
  const [assignmentError, setAssignmentError] = useState<string | null>(null)

  const [composerTab, setComposerTab] = useState<'reply' | 'note'>('reply')
  const [replyText, setReplyText] = useState('')
  const [replySending, setReplySending] = useState(false)
  const [replyStatus, setReplyStatus] = useState<SupportTicketStatus | ''>('')
  const [noteText, setNoteText] = useState('')
  const [noteSending, setNoteSending] = useState(false)
  const [composerError, setComposerError] = useState<string | null>(null)

  const [showLinkForm, setShowLinkForm] = useState(false)
  const [linkUserId, setLinkUserId] = useState('')
  const [linkingUser, setLinkingUser] = useState(false)

  const [macros, setMacros] = useState<SupportMacro[]>([])
  const [showMacroDropdown, setShowMacroDropdown] = useState(false)
  const [showMacroManager, setShowMacroManager] = useState(false)
  const [editingMacro, setEditingMacro] = useState<SupportMacro | null>(null)
  const [macroTitle, setMacroTitle] = useState('')
  const [macroBody, setMacroBody] = useState('')
  const [savingMacro, setSavingMacro] = useState(false)
  const [selectedMacroId, setSelectedMacroId] = useState<string | null>(null)

  const isAdmin = user?.role === 'Admin'
  const isSupportAdmin = user?.role === 'SupportAdmin'
  const isSupportAgent = user?.role === 'SupportAgent'
  const canReassign = isAdmin || isSupportAdmin
  const canManageMacros = isAdmin || isSupportAdmin

  // Tracks the routed ticket so a response for a ticket the agent has left is dropped. Otherwise the page
  // could show one customer while replies go to the routed ticket (BH-16).
  const currentTicketIdRef = useRef(ticketId)
  useLayoutEffect(() => {
    currentTicketIdRef.current = ticketId
  }, [ticketId])

  const loadTicketDetail = useCallback(async (id: string) => {
    try {
      const detail = await getSupportTicket(id)
      if (id === currentTicketIdRef.current) setTicketDetail(detail)
    } catch (err) {
      console.error('Failed to load ticket detail', err)
    } finally {
      if (id === currentTicketIdRef.current) setDetailLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!ticketId) return
    setTicketDetail(null)
    setDetailLoading(true)
    void loadTicketDetail(ticketId)
    getSupportMacros().then(setMacros).catch((err) => console.error('Failed to load macros', err))
    getSupportAgents()
      .then(setAgents)
      .catch((err) => {
        console.error('Failed to load support agents', err)
        setAssignmentError('Unable to load available assignees.')
      })
  }, [ticketId, loadTicketDetail])

  useEffect(() => {
    if (!ticketId) return
    const refreshTicketOnFocus = () => {
      void loadTicketDetail(ticketId)
    }
    window.addEventListener('focus', refreshTicketOnFocus)
    return () => window.removeEventListener('focus', refreshTicketOnFocus)
  }, [ticketId, loadTicketDetail])

  useEffect(() => {
    setComposerTab('reply')
    setReplyText('')
    setReplyStatus('')
    setNoteText('')
    setComposerError(null)
    setShowLinkForm(false)
    setLinkUserId('')
    setShowMacroDropdown(false)
    setSelectedMacroId(null)
    setAssignmentError(null)
  }, [ticketId])

  const handleSendReply = async () => {
    if (!ticketId || !replyText.trim() || replySending) return
    setReplySending(true)
    setComposerError(null)
    try {
      await sendAgentMessage(ticketId, {
        body: replyText.trim(),
        status: replyStatus || undefined,
        macroId: selectedMacroId || undefined
      })
      setReplyText('')
      setReplyStatus('')
      setSelectedMacroId(null)
      await loadTicketDetail(ticketId)
    } catch (err) {
      console.error('Failed to send reply', err)
      setComposerError('The reply was not sent. Check your connection and try again.')
    } finally {
      setReplySending(false)
    }
  }

  const handleAddNote = async () => {
    if (!ticketId || !noteText.trim() || noteSending) return
    setNoteSending(true)
    setComposerError(null)
    try {
      await addTicketNote(ticketId, noteText.trim())
      setNoteText('')
      setComposerTab('reply')
      await loadTicketDetail(ticketId)
    } catch (err) {
      console.error('Failed to add note', err)
      setComposerError('The note was not saved. Try again.')
    } finally {
      setNoteSending(false)
    }
  }

  const handleAssign = async (agentId: string) => {
    if (!ticketId || !ticketDetail || !agentId || assignmentSaving) return
    setAssignmentSaving(true)
    setAssignmentError(null)
    try {
      const updatedTicket = await assignTicket(ticketId, agentId, ticketDetail.ticket.assignmentRevision)
      setTicketDetail((current) => (current ? { ...current, ticket: updatedTicket } : current))
    } catch (err) {
      console.error('Failed to assign ticket', err)
      setAssignmentError('Unable to update the ticket assignee.')
    } finally {
      setAssignmentSaving(false)
    }
  }

  const handleUnassign = async () => {
    if (!ticketId || !ticketDetail || assignmentSaving) return
    setAssignmentSaving(true)
    setAssignmentError(null)
    try {
      const updatedTicket = await unassignTicket(ticketId, ticketDetail.ticket.assignmentRevision)
      setTicketDetail((current) => (current ? { ...current, ticket: updatedTicket } : current))
    } catch (err) {
      console.error('Failed to unassign ticket', err)
      setAssignmentError('Unable to update the ticket assignee.')
    } finally {
      setAssignmentSaving(false)
    }
  }

  const handleUpdateStatus = async (status: SupportTicketStatus) => {
    if (!ticketId) return
    try {
      await updateTicket(ticketId, { status })
      await loadTicketDetail(ticketId)
    } catch (err) {
      console.error('Failed to update status', err)
    }
  }

  const handleLinkUser = async () => {
    if (!ticketId || !linkUserId.trim() || linkingUser) return
    setLinkingUser(true)
    try {
      await linkUserToTicket(ticketId, linkUserId.trim())
      setLinkUserId('')
      setShowLinkForm(false)
      await loadTicketDetail(ticketId)
    } catch (err) {
      console.error('Failed to link user', err)
    } finally {
      setLinkingUser(false)
    }
  }

  const handleUnlinkUser = async (userId: string) => {
    if (!ticketId) return
    try {
      await unlinkUserFromTicket(ticketId, userId)
      await loadTicketDetail(ticketId)
    } catch (err) {
      console.error('Failed to unlink user', err)
    }
  }

  const handleAddTag = async (tag: string) => {
    if (!ticketId || !ticketDetail) return
    const currentTags = ticketDetail.ticket.tags || []
    if (currentTags.some((existing) => existing.toLowerCase() === tag.toLowerCase())) return
    try {
      await updateTicketTags(ticketId, [...currentTags, tag])
      await loadTicketDetail(ticketId)
    } catch (err) {
      console.error('Failed to add tag', err)
    }
  }

  const handleRemoveTag = async (tag: string) => {
    if (!ticketId || !ticketDetail) return
    try {
      await updateTicketTags(ticketId, (ticketDetail.ticket.tags || []).filter((existing) => existing !== tag))
      await loadTicketDetail(ticketId)
    } catch (err) {
      console.error('Failed to remove tag', err)
    }
  }

  const handleApplyMacro = (macro: SupportMacro) => {
    setComposerTab('reply')
    setReplyText(macro.body)
    setSelectedMacroId(macro.id)
    setShowMacroDropdown(false)
  }

  const handleSaveMacro = async () => {
    if (!macroTitle.trim() || !macroBody.trim() || savingMacro) return
    setSavingMacro(true)
    try {
      if (editingMacro) {
        await updateSupportMacro(editingMacro.id, macroTitle.trim(), macroBody.trim())
      } else {
        await createSupportMacro(macroTitle.trim(), macroBody.trim())
      }
      setMacros(await getSupportMacros())
      setEditingMacro(null)
      setMacroTitle('')
      setMacroBody('')
    } catch (err) {
      console.error('Failed to save macro', err)
    } finally {
      setSavingMacro(false)
    }
  }

  const handleDeleteMacro = async (macroId: string) => {
    if (!confirm('Are you sure you want to delete this macro?')) return
    try {
      await deleteSupportMacro(macroId)
      setMacros(await getSupportMacros())
      if (editingMacro?.id === macroId) {
        setEditingMacro(null)
        setMacroTitle('')
        setMacroBody('')
      }
    } catch (err) {
      console.error('Failed to delete macro', err)
    }
  }

  const timeline = useMemo<TimelineEntry[]>(() => {
    if (!ticketDetail) return []
    const entries: TimelineEntry[] = [
      ...ticketDetail.messages.map((message): TimelineEntry => ({ kind: 'message', at: message.createdAt, message })),
      ...ticketDetail.notes.map((note): TimelineEntry => ({ kind: 'note', at: note.createdAt, note }))
    ]
    return entries.sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime())
  }, [ticketDetail])

  const assignedAgentId = ticketDetail?.ticket.assignedAgentId ?? null
  const assignedAgentName = agentNameById(agents, assignedAgentId)
  const isAssignedToCurrentUser = Boolean(assignedAgentId && assignedAgentId === user?.id)
  const assignableAgents = canReassign
    ? agents
    : isSupportAgent && (!assignedAgentId || isAssignedToCurrentUser)
      ? agents.filter((agent) => agent.id === user?.id || agent.role === 'Admin' || agent.role === 'SupportAdmin')
      : []
  const hasCurrentAgentOption = Boolean(assignedAgentId && assignableAgents.some((agent) => agent.id === assignedAgentId))

  const customerName = ticketDetail?.user?.username || 'Customer'
  const resolved = ticketDetail ? bucketForStatus(ticketDetail.ticket.status) === 'resolved' : false

  if (detailLoading) {
    return <p className="p-10 text-center text-sm text-muted-foreground">Loading ticket…</p>
  }

  if (!ticketDetail) {
    return (
      <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">
        <MessageSquare className="mx-auto mb-3 h-8 w-8 opacity-40" />
        <p className="text-sm font-medium text-foreground">Ticket unavailable</p>
        <p className="mt-1 text-xs">Return to the queue and choose another ticket.</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate('/support')}>
          <ArrowLeft className="mr-1 h-3 w-3" />
          Back to queue
        </Button>
      </div>
    )
  }

  const { ticket } = ticketDetail

  return (
    <div className="pb-10">
      {/* Header strip */}
      <div className="mb-4 flex flex-wrap items-center gap-3 border-b border-border/60 pb-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/support')} className="gap-1.5">
          <ArrowLeft className="h-4 w-4" />
          Queue
        </Button>
        <span className="font-mono text-xs text-muted-foreground">{ticketRef(ticket.id)}</span>
        <TicketStatusBadge status={ticket.status} />
        <span className="text-sm font-semibold">{ticket.category || 'General'}</span>
        <AgeIndicator since={ticket.lastMessageAt} muted={resolved} />
        <span className="flex-1" />
        {!resolved ? (
          <Button size="sm" onClick={() => void handleUpdateStatus('Resolved')}>
            <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
            Resolve
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={() => void handleUpdateStatus('WaitingForAgent')}>
            Reopen
          </Button>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        {/* Conversation */}
        <div className="min-w-0">
          <div className="space-y-4">
            {timeline.map((entry, index) => {
              const previous = index > 0 ? timeline[index - 1] : null
              const showDayDivider = !previous || localDayKey(previous.at) !== localDayKey(entry.at)
              return (
                <div key={entry.kind === 'message' ? entry.message.id : entry.note.id} className="space-y-4">
                  {showDayDivider && (
                    <div className="flex items-center gap-3 font-mono text-[11px] text-muted-foreground/70">
                      <span className="h-px flex-1 bg-border/60" />
                      {formatDay(entry.at)}
                      <span className="h-px flex-1 bg-border/60" />
                    </div>
                  )}

                  {entry.kind === 'note' ? (
                    <div className="flex gap-3">
                      <InitialsAvatar name={agentNameById(agents, entry.note.authorAgentId)} size="h-7 w-7" />
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex items-baseline gap-2">
                          <span className="text-xs font-semibold text-amber-800">
                            {agentNameById(agents, entry.note.authorAgentId)}
                          </span>
                          <span className="rounded border border-amber-300 bg-amber-50 px-1.5 font-mono text-[10px] uppercase tracking-wide text-amber-700">
                            Internal note
                          </span>
                          <span className="ml-auto font-mono text-[11px] text-muted-foreground">{formatTime(entry.at)}</span>
                        </div>
                        <p className="whitespace-pre-wrap rounded-lg border border-dashed border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                          {entry.note.body}
                        </p>
                      </div>
                    </div>
                  ) : entry.message.senderType === 'System' ? (
                    <p className="text-center font-mono text-[11px] text-muted-foreground/70">
                      {entry.message.body}
                    </p>
                  ) : (
                    <div className="flex gap-3">
                      {entry.message.senderType === 'Customer' ? (
                        <InitialsAvatar name={customerName} imageUrl={ticketDetail.user?.profileImageUrl} size="h-7 w-7" />
                      ) : (
                        <InitialsAvatar name="Agent" size="h-7 w-7" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex items-baseline gap-2">
                          <span className="text-xs font-semibold">
                            {entry.message.senderType === 'Customer' ? customerName : 'Agent'}
                          </span>
                          <span className="text-[11px] text-muted-foreground">
                            {entry.message.senderType === 'Customer' ? 'Customer' : 'HikeStrong support'}
                          </span>
                          <span className="ml-auto font-mono text-[11px] text-muted-foreground">{formatTime(entry.at)}</span>
                        </div>
                        <div
                          className={`rounded-lg rounded-tl-sm border p-3 ${
                            entry.message.senderType === 'Agent'
                              ? 'border-transparent bg-primary/10'
                              : 'border-border/50 bg-muted/40'
                          }`}
                        >
                          <p className="whitespace-pre-wrap text-sm">{entry.message.body}</p>
                          <MessageAttachments message={entry.message} />
                        </div>
                        {entry.message.diagnosticsIncluded && (
                          <p className="mt-1 font-mono text-[10px] text-muted-foreground/70">Diagnostics attached</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Composer */}
          <div className="mt-6">
            <div className="relative z-10 -mb-px flex gap-1">
              <button
                type="button"
                onClick={() => setComposerTab('reply')}
                className={`rounded-t-lg border border-b-0 px-4 py-1.5 text-xs font-semibold transition ${
                  composerTab === 'reply' ? 'border-border bg-card text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                Reply to {customerName}
              </button>
              <button
                type="button"
                onClick={() => setComposerTab('note')}
                className={`rounded-t-lg border border-b-0 px-4 py-1.5 text-xs font-semibold transition ${
                  composerTab === 'note' ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                Internal note
              </button>
            </div>

            {composerTab === 'reply' ? (
              <div className="rounded-lg rounded-tl-none border border-border bg-card">
                {selectedMacroId && (
                  <div className="flex items-center gap-2 border-b border-border/50 px-3 py-1.5">
                    <FileText className="h-3 w-3 text-muted-foreground" />
                    <span className="flex-1 text-xs text-muted-foreground">
                      Using macro: {macros.find((macro) => macro.id === selectedMacroId)?.title}
                    </span>
                    <button type="button" onClick={() => setSelectedMacroId(null)} className="text-muted-foreground hover:text-foreground">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                )}
                <textarea
                  className="block w-full resize-none bg-transparent p-3 text-sm focus:outline-none"
                  rows={3}
                  placeholder="Type your reply..."
                  value={replyText}
                  // Locked while sending, so the sent reply is what's on screen (BH-14).
                  disabled={replySending}
                  onChange={(event) => {
                    setReplyText(event.target.value)
                    if (selectedMacroId) setSelectedMacroId(null)
                  }}
                  onKeyDown={(event) => {
                    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                      event.preventDefault()
                      void handleSendReply()
                    }
                  }}
                />
                <div className="flex flex-wrap items-center gap-2 border-t border-border/50 px-2 py-2">
                  <div className="relative">
                    <Button variant="ghost" size="sm" onClick={() => setShowMacroDropdown(!showMacroDropdown)} className="gap-1">
                      <FileText className="h-3 w-3" />
                      Macros
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                    {showMacroDropdown && (
                      <div className="absolute bottom-full left-0 z-10 mb-1 w-64 rounded-lg border border-border bg-popover shadow-lg">
                        <div className="max-h-48 overflow-y-auto p-1">
                          {macros.length === 0 ? (
                            <p className="p-3 text-center text-xs text-muted-foreground">No macros available</p>
                          ) : (
                            macros.map((macro) => (
                              <button
                                key={macro.id}
                                type="button"
                                onClick={() => handleApplyMacro(macro)}
                                className="w-full rounded-md px-3 py-2 text-left hover:bg-muted"
                              >
                                <span className="block text-sm font-medium">{macro.title}</span>
                                <span className="block truncate text-xs text-muted-foreground">{macro.body.slice(0, 60)}…</span>
                              </button>
                            ))
                          )}
                        </div>
                        {canManageMacros && (
                          <div className="border-t border-border p-1">
                            <button
                              type="button"
                              onClick={() => {
                                setShowMacroDropdown(false)
                                setShowMacroManager(true)
                                setEditingMacro(null)
                                setMacroTitle('')
                                setMacroBody('')
                              }}
                              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-xs text-muted-foreground hover:bg-muted"
                            >
                              <Settings className="h-3 w-3" />
                              Manage Macros
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <select
                    aria-label="Status after sending"
                    className="h-7 rounded-md border border-border bg-background px-2 text-xs text-muted-foreground"
                    value={replyStatus}
                    onChange={(event) => setReplyStatus(event.target.value as SupportTicketStatus | '')}
                    disabled={replySending}
                  >
                    <option value="">No status change</option>
                    {ticketStatusOptions.map((status) => (
                      <option key={status} value={status}>
                        On send: {statusLabel(status)}
                      </option>
                    ))}
                  </select>
                  <span className="ml-auto font-mono text-[10px] text-muted-foreground/70">⌘↵ to send</span>
                  <Button size="sm" onClick={() => void handleSendReply()} disabled={!replyText.trim() || replySending}>
                    <Send className="mr-1 h-3 w-3" />
                    {replySending ? 'Sending…' : 'Send reply'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="rounded-lg rounded-tl-none border border-amber-300 bg-amber-50/60">
                <textarea
                  className="block w-full resize-none bg-transparent p-3 text-sm text-amber-950 placeholder:text-amber-700/60 focus:outline-none"
                  rows={3}
                  placeholder="Internal note (not visible to customer)..."
                  value={noteText}
                  onChange={(event) => setNoteText(event.target.value)}
                  disabled={noteSending}
                  onKeyDown={(event) => {
                    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                      event.preventDefault()
                      void handleAddNote()
                    }
                  }}
                />
                <div className="flex items-center justify-between gap-2 border-t border-amber-200 px-2 py-2">
                  <span className="px-2 text-[11px] text-amber-700">Only agents can see internal notes.</span>
                  <Button size="sm" onClick={() => void handleAddNote()} disabled={!noteText.trim() || noteSending}>
                    {noteSending ? 'Adding…' : 'Add note'}
                  </Button>
                </div>
              </div>
            )}
            {composerError && <p className="mt-2 text-xs text-red-600">{composerError}</p>}
          </div>
        </div>

        {/* Context sidebar */}
        <aside className="space-y-4 lg:border-l lg:border-border/60 lg:pl-5">
          <SidebarSection title="Customer">
            {ticketDetail.user ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2.5">
                  <InitialsAvatar name={ticketDetail.user.username} imageUrl={ticketDetail.user.profileImageUrl} size="h-9 w-9" />
                  <div className="min-w-0">
                    <Link
                      to={`/users/mobile/${encodeURIComponent(ticketDetail.user.userId)}`}
                      className="block truncate text-sm font-semibold hover:underline"
                    >
                      {ticketDetail.user.username || ticketDetail.user.userId}
                    </Link>
                    <span className="block truncate font-mono text-[10px] text-muted-foreground">{ticketDetail.user.userId}</span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <PlanBadge tier={ticketDetail.user.subscriptionTier} status={ticketDetail.user.subscriptionStatus} />
                </div>
                {ticketDetail.user.contactEmail ? (
                  <div className="space-y-1.5">
                    <p className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                      <Mail className="h-3 w-3 shrink-0" />
                      <span className="truncate" title={ticketDetail.user.contactEmail}>{ticketDetail.user.contactEmail}</span>
                    </p>
                    <Button asChild variant="outline" size="sm" className="h-7 px-2">
                      <a href={customerEmailHref(ticketDetail.user.contactEmail, ticket.id)}>
                        <Mail className="mr-1 h-3 w-3" />
                        Email customer
                      </a>
                    </Button>
                  </div>
                ) : (
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Mail className="h-3 w-3 shrink-0" />
                    No contactable email on this account.
                  </p>
                )}
                <dl className="space-y-1 text-xs">
                  <div className="flex justify-between gap-2">
                    <dt className="text-muted-foreground">Member since</dt>
                    <dd className="font-mono">{formatDateTime(ticketDetail.user.createdAt)}</dd>
                  </div>
                  <div className="flex justify-between gap-2">
                    <dt className="text-muted-foreground">Last active</dt>
                    <dd className="font-mono">{formatDateTime(ticketDetail.user.lastLoginAt)}</dd>
                  </div>
                </dl>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                <span className="font-mono">{ticket.userId}</span> — user not found.
              </p>
            )}
          </SidebarSection>

          <SidebarSection title="Assignee">
            <div className="space-y-2">
              {assignedAgentId ? (
                <div className="flex items-center gap-2">
                  <InitialsAvatar name={assignedAgentName} size="h-7 w-7" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">
                      {isAssignedToCurrentUser ? `${assignedAgentName} (you)` : assignedAgentName}
                    </p>
                    {ticket.assignedAt && (
                      <p className="font-mono text-[10px] text-muted-foreground">since {formatDateTime(ticket.assignedAt)}</p>
                    )}
                  </div>
                  {(isAssignedToCurrentUser || canReassign) && (
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => void handleUnassign()} disabled={assignmentSaving}>
                      Unassign
                    </Button>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-muted-foreground">Unassigned</span>
                  <Button variant="outline" size="sm" className="h-7" onClick={() => void handleAssign('me')} disabled={assignmentSaving}>
                    Assign to me
                  </Button>
                </div>
              )}
              {assignableAgents.length > 0 && (
                <select
                  aria-label="Assign ticket to"
                  value={assignedAgentId ?? ''}
                  onChange={(event) => {
                    const nextAgentId = event.target.value
                    if (nextAgentId && nextAgentId !== assignedAgentId) {
                      void handleAssign(nextAgentId)
                    }
                  }}
                  disabled={assignmentSaving}
                  className="h-8 w-full rounded-md border border-border bg-background px-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Choose an assignee…</option>
                  {assignedAgentId && !hasCurrentAgentOption && <option value={assignedAgentId}>{assignedAgentName}</option>}
                  {assignableAgents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {supportAgentLabel(agent)} ({agent.role})
                    </option>
                  ))}
                </select>
              )}
              {assignmentError && (
                <p role="alert" className="text-xs text-red-600">
                  {assignmentError}
                </p>
              )}
            </div>
          </SidebarSection>

          <SidebarSection title="Status">
            <select
              aria-label="Set ticket status"
              value={ticket.status === 'Open' ? 'WaitingForAgent' : ticket.status === 'WaitingOnCustomer' ? 'WaitingForCustomer' : ticket.status}
              onChange={(event) => void handleUpdateStatus(event.target.value as SupportTicketStatus)}
              className="h-8 w-full rounded-md border border-border bg-background px-2 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {ticketStatusOptions.map((status) => (
                <option key={status} value={status}>
                  {statusLabel(status)}
                </option>
              ))}
            </select>
          </SidebarSection>

          <SidebarSection title="Tags">
            <div className="flex flex-wrap gap-1.5">
              {(ticket.tags || []).map((tag) => (
                <span key={tag} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${tagChipClass(tag)}`}>
                  {tag}
                  <button
                    type="button"
                    onClick={() => void handleRemoveTag(tag)}
                    className="rounded-full p-0.5 hover:bg-black/10"
                    title="Remove tag"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </span>
              ))}
              {predefinedTags
                .filter((tag) => !(ticket.tags || []).some((existing) => existing.toLowerCase() === tag.toLowerCase()))
                .map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => void handleAddTag(tag)}
                    className="rounded-full border border-dashed border-border px-2 py-0.5 text-[11px] text-muted-foreground transition hover:border-primary hover:text-primary"
                  >
                    + {tag}
                  </button>
                ))}
            </div>
          </SidebarSection>

          <SidebarSection title="Device snapshot">
            <dl className="space-y-1 text-xs">
              <div className="flex justify-between gap-2">
                <dt className="flex items-center gap-1.5 text-muted-foreground">
                  <BatteryMedium className="h-3.5 w-3.5" />
                  Battery
                </dt>
                <dd className="font-mono">{formatBatteryLevel(ticket.batteryLevelPercent)}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Power mode</dt>
                <dd className={`font-mono ${ticket.batterySaverMode ? 'text-amber-700' : ''}`}>{batteryModeLabel(ticket.batterySaverMode)}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Ticket created</dt>
                <dd className="font-mono">{formatDateTime(ticket.createdAt)}</dd>
              </div>
            </dl>
          </SidebarSection>

          <SidebarSection
            title="Linked accounts"
            action={
              !showLinkForm ? (
                <Button variant="ghost" size="sm" onClick={() => setShowLinkForm(true)} className="h-6 px-2 text-xs">
                  <Plus className="mr-1 h-3 w-3" />
                  Link
                </Button>
              ) : undefined
            }
          >
            {showLinkForm && (
              <div className="mb-2 flex items-center gap-1.5">
                <Input
                  placeholder="User ID…"
                  value={linkUserId}
                  onChange={(event) => setLinkUserId(event.target.value)}
                  className="h-7 flex-1 text-xs"
                  onKeyDown={(event) => event.key === 'Enter' && handleLinkUser()}
                />
                <Button size="sm" onClick={() => void handleLinkUser()} disabled={!linkUserId.trim() || linkingUser} className="h-7 px-2 text-xs">
                  {linkingUser ? '…' : 'Link'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => {
                    setShowLinkForm(false)
                    setLinkUserId('')
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
            {ticket.linkedUserIds.length === 0 ? (
              <p className="text-xs text-muted-foreground">None linked.</p>
            ) : (
              <div className="space-y-1.5">
                {(ticketDetail.linkedUsers.length > 0
                  ? ticketDetail.linkedUsers.map((linkedUser) => ({
                      userId: linkedUser.userId,
                      label: linkedUser.username || linkedUser.userId,
                      imageUrl: linkedUser.profileImageUrl
                    }))
                  : ticket.linkedUserIds.map((linkedId) => ({ userId: linkedId, label: linkedId, imageUrl: null }))
                ).map((linked) => (
                  <div key={linked.userId} className="flex items-center gap-2 rounded-lg border border-border/60 bg-background p-1.5">
                    <InitialsAvatar name={linked.label} imageUrl={linked.imageUrl} size="h-6 w-6" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium">{linked.label}</p>
                      <p className="truncate font-mono text-[10px] text-muted-foreground">{linked.userId}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleUnlinkUser(linked.userId)}
                      className="rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                      title="Remove linked account"
                    >
                      <UserMinus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </SidebarSection>
        </aside>
      </div>

      {/* Macro Manager Modal */}
      {showMacroManager && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 flex max-h-[80vh] w-full max-w-3xl flex-col rounded-2xl bg-background shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <div>
                <h2 className="text-lg font-semibold">Manage Macros</h2>
                <p className="text-sm text-muted-foreground">Create and edit reply templates</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowMacroManager(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex flex-1 overflow-hidden">
              <div className="w-64 shrink-0 border-r border-border">
                <div className="p-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start gap-2"
                    onClick={() => {
                      setEditingMacro(null)
                      setMacroTitle('')
                      setMacroBody('')
                    }}
                  >
                    <Plus className="h-3 w-3" />
                    New Macro
                  </Button>
                </div>
                <div className="max-h-80 overflow-y-auto px-2 pb-2">
                  {macros.length === 0 ? (
                    <div className="p-3 text-center text-xs text-muted-foreground">
                      <p>No macros yet</p>
                      <p className="mt-1">Create a macro when the team needs a reusable reply.</p>
                    </div>
                  ) : (
                    macros.map((macro) => (
                      <button
                        key={macro.id}
                        type="button"
                        onClick={() => {
                          setEditingMacro(macro)
                          setMacroTitle(macro.title)
                          setMacroBody(macro.body)
                        }}
                        className={`mb-1 w-full rounded-lg px-3 py-2 text-left transition ${
                          editingMacro?.id === macro.id ? 'bg-primary/10 text-primary' : 'hover:bg-muted'
                        }`}
                      >
                        <span className="block truncate text-sm font-medium">{macro.title}</span>
                        <span className="block truncate text-xs text-muted-foreground">{macro.body.slice(0, 40)}…</span>
                      </button>
                    ))
                  )}
                </div>
              </div>

              <div className="flex flex-1 flex-col p-4">
                <div className="mb-3">
                  <label htmlFor="macro-title" className="mb-1 block text-xs font-medium text-muted-foreground">
                    Macro Title
                  </label>
                  <Input
                    id="macro-title"
                    placeholder="e.g., Greeting, Refund Policy, Follow-up..."
                    value={macroTitle}
                    onChange={(event) => setMacroTitle(event.target.value)}
                  />
                </div>
                <div className="mb-3 flex-1">
                  <label htmlFor="macro-content" className="mb-1 block text-xs font-medium text-muted-foreground">
                    Macro Content
                  </label>
                  <textarea
                    id="macro-content"
                    className="h-48 w-full rounded-lg border border-border bg-background p-3 text-sm focus:border-primary focus:outline-none"
                    placeholder="Enter the template text...&#10;&#10;You can use placeholders:&#10;{firstName} - Customer's first name&#10;{ticketId} - Ticket ID&#10;{platform} - Customer's platform&#10;{appVersion} - Customer's app version"
                    value={macroBody}
                    onChange={(event) => setMacroBody(event.target.value)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  {editingMacro && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={() => void handleDeleteMacro(editingMacro.id)}
                    >
                      <Trash2 className="mr-1 h-3 w-3" />
                      Delete
                    </Button>
                  )}
                  <div className="ml-auto flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setEditingMacro(null)
                        setMacroTitle('')
                        setMacroBody('')
                      }}
                    >
                      Clear
                    </Button>
                    <Button size="sm" onClick={() => void handleSaveMacro()} disabled={!macroTitle.trim() || !macroBody.trim() || savingMacro}>
                      {savingMacro ? 'Saving…' : editingMacro ? 'Update Macro' : 'Create Macro'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
